import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import mixitup from 'mixitup';
import jQuery from 'jquery';
import App from './App';
import Barba from '@barba/core';

// Register GSAP plugins
gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);

const Shop_C7 = {
  init: function () {
    console.log('Shop_C7: initializing');

    // Ensure the navigation has the outside-hero class on load
    const ensureOutsideHeroClass = () => {
      // Check if we're on a shop or product page
      const path = window.location.pathname;
      if (!path.startsWith('/shop') && !path.startsWith('/product')) {
        return;
      }

      const siteNav = document.querySelector('#site-navigation');
      if (siteNav) {
        siteNav.classList.add('outside-hero');
        siteNav.classList.remove('inside-hero');
        console.log('Applied outside-hero class to site navigation on shop/product page');
      }
    };

    // Apply immediately if on correct page
    ensureOutsideHeroClass();

    // Also apply on DOMContentLoaded if on correct page
    document.addEventListener('DOMContentLoaded', ensureOutsideHeroClass);

    // Handle URL hash navigation after page load
    this.handleHashNavigation();

    // And also apply on window load
    window.addEventListener('load', () => {
      ensureOutsideHeroClass();
      // Handle hash navigation again after load
      this.handleHashNavigation();
      // Refresh ScrollTrigger after ensuring proper classes
      setTimeout(() => {
        ScrollTrigger.refresh();
      }, 100);
    });

    // Initialize smooth scrolling for collection navigation
    this.initCollectionNavigation();

    // Initialize collection section observer
    this.initCollectionObserver();

    // Initialize sub nav pinning
    this.initSubNavPinning();

    // Initialize content loaded observer
    this.initContentLoadedObserver();

    // Initialize mobile navigation
    this.initMobileNav();

    // Initialize cart count observer
    this.initCartCountObserver();

    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 5000);
  },

  // Function to observe and update cart count
  initCartCountObserver: function () {
    console.log('Shop_C7: initializing cart count observer');

    // Function to update the cart count in the header
    const updateCartCount = (count) => {
      console.log('Updating cart count to:', count);
      const cartCountElement = document.querySelector('.cartCount');
      const cartCountContainer = document.querySelector('.cartCount-container');

      if (cartCountElement && cartCountContainer) {
        // Convert count to number and display without leading zeros
        const numCount = parseInt(count, 10);
        cartCountElement.textContent = numCount.toString();

        // Animate opacity based on cart content
        if (numCount > 0) {
          gsap.to(cartCountContainer, {
            opacity: 1,
            duration: 0.3,
            ease: 'power2.out',
          });
        } else {
          gsap.to(cartCountContainer, {
            opacity: 0,
            duration: 0.3,
            ease: 'power2.out',
          });
        }
      }
    };

    // Initial check for cart count
    const checkCartCount = () => {
      // First try to get count from the cart count element
      const cartCountElement = document.querySelector('.c7-user-nav__cart__count');
      if (cartCountElement) {
        const count = cartCountElement.textContent.trim();
        if (count) {
          updateCartCount(count);
          return;
        }
      }

      // As a fallback, try to parse the count from the aria-label
      const cartButton = document.querySelector('.c7-user-nav__cart button');
      if (cartButton) {
        const ariaLabel = cartButton.getAttribute('aria-label');
        if (ariaLabel) {
          const match = ariaLabel.match(/contains\s+(\d+)\s+items?/i);
          if (match && match[1]) {
            updateCartCount(match[1]);
          } else {
            // If no items, set count to 0
            updateCartCount(0);
          }
        }
      }
    };

    // Set up a mutation observer to watch for changes to the cart
    const setupObserver = () => {
      // First check if C7 cart elements exist yet
      const c7CartElement = document.getElementById('c7-cart');

      if (c7CartElement) {
        // Create observer that will watch for cart updates
        const observer = new MutationObserver((mutations) => {
          // Check cart count when something changes
          checkCartCount();
        });

        // Start observing the cart area for any changes
        observer.observe(c7CartElement, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['aria-label', 'class'],
        });

        // Also do an initial check
        checkCartCount();
      } else {
        // If Commerce7 cart not found yet, keep trying
        setTimeout(setupObserver, 1000);
      }
    };

    // Start the observer setup
    setupObserver();

    // Also check whenever the page content changes significantly
    document.addEventListener('c7:cartUpdated', () => {
      console.log('C7 cart updated event detected');
      setTimeout(checkCartCount, 500); // Small delay to let DOM update
    });

    // Check after page loads
    window.addEventListener('load', () => {
      setTimeout(checkCartCount, 1000);
    });
  },

  initContentLoadedObserver: function () {
    const page = document.querySelector('.wine-collections-page');
    if (!page) return;

    // Create a mutation observer to watch for content changes
    const observer = new MutationObserver((mutations) => {
      // Check if all collection sections are loaded
      const collectionSections = page.querySelectorAll('.collection-section');
      const c7Collections = page.querySelectorAll('.c7-product-collection');

      if (collectionSections.length > 0 && c7Collections.length > 0) {
        // Check if all C7 collections have their products loaded
        const allCollectionsLoaded = Array.from(c7Collections).every((collection) => {
          return collection.querySelector('.c7-product-collection__product-list');
        });

        if (allCollectionsLoaded) {
          console.log('All C7 collections loaded');
          // Refresh ScrollTrigger
          ScrollTrigger.refresh();
          // Trigger hash navigation again if needed
          if (window.location.hash) {
            this.handleHashNavigation();
          }
          // Disconnect the observer since we're done
          observer.disconnect();
        }
      }
    });

    // Start observing the page for changes
    observer.observe(page, {
      childList: true,
      subtree: true,
    });
  },

  initSubNavPinning: function () {
    const header = document.querySelector('#site-navigation');
    const subNavWrapper = document.querySelector('.collections-sub-nav-wrapper');

    if (header && subNavWrapper) {
      // Apply the is-pinned class immediately on initialization
      subNavWrapper.classList.add('is-pinned');

      // Position the subnav right below the header
      const updatePosition = () => {
        const headerHeight = header.offsetHeight;
        const headerBottom = header.getBoundingClientRect().bottom;

        // Set the top position of the subnav to be at the bottom of the header
        subNavWrapper.style.position = 'fixed';
        subNavWrapper.style.top = `${headerBottom + 5}px`;
        subNavWrapper.style.left = '0';
        subNavWrapper.style.width = '100%';
        subNavWrapper.style.zIndex = '99';
      };

      // Update position immediately
      updatePosition();

      // Update position on resize and scroll
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);
    }
  },

  // Modify the calculateHeaderOffset function to be more precise
  calculateHeaderOffset: function () {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      const header = document.querySelector('#site-navigation');
      const subNav = document.querySelector('.mobile-collections-nav');
      const headerHeight = header ? header.offsetHeight : 0;

      // Only include subNav height if it's not open (when open it shouldn't affect offset)
      const subNavHeight = subNav && !subNav.querySelector('.is-open') ? subNav.offsetHeight : 0;

      // Reduced padding for mobile
      const totalOffset = headerHeight + subNavHeight + 100;

      console.log('Mobile offset calculation:', {
        headerHeight,
        subNavHeight,
        totalOffset,
        isSubNavOpen: subNav ? subNav.querySelector('.is-open') !== null : false,
      });
      return totalOffset;
    }
    return 100; // Default desktop offset
  },

  // Modify the scroll function to handle viewport positions better
  scrollToElement: function (targetElement, useAnimation = true) {
    if (!targetElement) return;

    // Wait for a frame to ensure layout is stable
    requestAnimationFrame(() => {
      const headerOffset = this.calculateHeaderOffset();
      const rect = targetElement.getBoundingClientRect();

      // Calculate position relative to viewport
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const absoluteTop = rect.top + scrollTop;

      // Ensure we're not scrolling past the bottom of the page
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const targetPosition = Math.min(absoluteTop - headerOffset, maxScroll);

      console.log('Scroll calculation:', {
        elementTop: rect.top,
        absoluteTop,
        scrollTop,
        headerOffset,
        targetPosition,
        viewportHeight: window.innerHeight,
        elementHeight: rect.height,
      });

      if (useAnimation) {
        gsap.to(window, {
          duration: 0.8,
          scrollTo: {
            y: targetPosition - (window.innerWidth >= 768 ? 100 : 0),
            autoKill: false, // Prevents scroll interruption
          },
          ease: 'power2.inOut',
          onComplete: () => {
            console.log('Scroll animation completed to:', targetPosition);
            // Double-check position after animation
            setTimeout(() => {
              const newRect = targetElement.getBoundingClientRect();
              const desiredOffset = headerOffset + (window.innerWidth >= 768 ? 100 : 0);
              if (Math.abs(newRect.top - desiredOffset) > 10) {
                // If position is off by more than 10px, adjust while maintaining the offset
                window.scrollTo({
                  top: window.pageYOffset + (newRect.top - desiredOffset),
                  behavior: 'instant',
                });
              }
            }, 100);
          },
        });
      } else {
        window.scrollTo({
          top: targetPosition - (window.innerWidth >= 768 ? 200 : 0),
          behavior: 'instant',
        });
      }
    });
  },

  // Modify initCollectionNavigation to handle mobile better
  initCollectionNavigation: function () {
    const navLinks = document.querySelectorAll('.collections-sub-nav a, .mobile-collections-nav a');

    navLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('Navigation link clicked:', link.getAttribute('href'));

        const targetId = link.getAttribute('href');
        const targetElement = document.querySelector(targetId);

        if (targetElement) {
          // For mobile, ensure dropdown is closed before scrolling
          const mobileNav = document.querySelector('.mobile-collections-nav');
          const isMobile = window.innerWidth < 768;

          if (isMobile && mobileNav) {
            const trigger = mobileNav.querySelector('.mobile-nav-trigger');
            const navList = mobileNav.querySelector('ul');
            if (trigger && navList) {
              trigger.classList.remove('is-open');
              navList.classList.remove('is-open');
            }
          }

          // Wait for Commerce7 content and layout to stabilize
          const waitForC7Content = setInterval(() => {
            const c7Collection = targetElement.querySelector(
              '.c7-product-collection__product-list'
            );
            if (c7Collection) {
              clearInterval(waitForC7Content);
              console.log('C7 content found for:', targetId);

              // Update URL hash without triggering scroll
              history.pushState(null, null, targetId);

              // Give layout time to stabilize after closing mobile nav
              setTimeout(
                () => {
                  this.scrollToElement(targetElement);
                },
                isMobile ? 150 : 0
              );
            }
          }, 100);

          // Set a timeout to stop checking after 5 seconds
          setTimeout(() => {
            clearInterval(waitForC7Content);
            console.log('Timeout reached waiting for C7 content:', targetId);
          }, 5000);
        }
      });
    });
  },

  initCollectionObserver: function () {
    // Create a mutation observer to watch for the first collection section
    const observer = new MutationObserver((mutations) => {
      // Look specifically for the first collection section
      const firstSection = document.querySelector('.collection-section');

      if (firstSection) {
        // Add first-section class to the first collection section
        firstSection.classList.add('first-section');

        // Disconnect the observer since we've found and marked the first section
        observer.disconnect();
      }
    });

    // Start observing the container for changes
    const container = document.querySelector('.container');
    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
      });
    }
  },

  initMobileNav: function () {
    const mobileNav = document.querySelector('.mobile-collections-nav');
    if (!mobileNav) return;

    const trigger = mobileNav.querySelector('.mobile-nav-trigger');
    const navList = mobileNav.querySelector('ul');
    if (!trigger || !navList) return;

    // Handle trigger click
    trigger.addEventListener('click', () => {
      trigger.classList.toggle('is-open');
      navList.classList.toggle('is-open');
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!mobileNav.contains(e.target)) {
        trigger.classList.remove('is-open');
        navList.classList.remove('is-open');
      }
    });

    // Close dropdown when clicking a link
    navList.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        trigger.classList.remove('is-open');
        navList.classList.remove('is-open');
      });
    });
  },

  // Modify handleHashNavigation to use the new scroll function
  handleHashNavigation: function () {
    if (!window.location.hash) return;

    const hash = window.location.hash.substring(1);
    console.log('Handling hash navigation to:', hash);

    // Create a promise that resolves when all C7 collections are loaded
    const waitForC7Collections = () => {
      return new Promise((resolve) => {
        let checkCount = 0;
        const maxChecks = 50;

        const checkCollections = () => {
          const collections = document.querySelectorAll('.c7-product-collection');
          const loadedCollections = document.querySelectorAll(
            '.c7-product-collection__product-list'
          );

          console.log(
            `Checking C7 collections: ${loadedCollections.length}/${collections.length} loaded`
          );

          if (collections.length > 0 && collections.length === loadedCollections.length) {
            resolve(true);
          } else if (checkCount >= maxChecks) {
            console.log('Timeout waiting for C7 collections');
            resolve(false);
          } else {
            checkCount++;
            setTimeout(checkCollections, 100);
          }
        };

        checkCollections();
      });
    };

    // Main navigation handler
    const handleNavigation = async () => {
      const targetElement = document.getElementById(hash);
      if (!targetElement) return;

      // First scroll to approximate position without animation
      this.scrollToElement(targetElement, false);

      // Wait for C7 collections to load
      const collectionsLoaded = await waitForC7Collections();

      if (collectionsLoaded) {
        // Give a small delay for final layout adjustments
        setTimeout(() => {
          ScrollTrigger.refresh();
          // Final scroll with animation
          this.scrollToElement(targetElement, true);
        }, 300);
      }
    };

    // Set up mutation observer to watch for C7 content changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'childList' &&
          mutation.addedNodes.length > 0 &&
          mutation.target.classList.contains('c7-product-collection')
        ) {
          handleNavigation();
        }
      });
    });

    // Start observing the container for C7 content changes
    const container = document.querySelector('.container');
    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
      });

      // Initial navigation attempt
      handleNavigation();

      // Cleanup observer after reasonable timeout
      setTimeout(() => {
        observer.disconnect();
      }, 10000);
    }
  },
};

export default Shop_C7;
